'use client';
import { useEffect, useMemo, useState } from 'react';
import type { VelocityFile, FitFile, FitRow, VelocityRow } from '@/lib/research/types';
import type { ResearchNotesFile } from '@/lib/research/state-machine';
import { reconstructAtDate, notedDates } from '@/lib/research/history';
import { IdeaCard } from '@/components/research/IdeaCard';
import { SignalDot } from '@/components/SignalDot';
import { deriveRating } from '@/lib/research/state-machine';
import { TrackRecord } from '@/components/research/TrackRecord';
import { OpenIdeasTable } from '@/components/research/OpenIdeasTable';
import { MoversTable } from '@/components/research/MoversTable';
import { MoversTab } from './_components/MoversTab';
import { AlphaZoneReports } from '@/components/research/AlphaZoneReports';
import { PeerGroupTrends } from '@/components/research/PeerGroupTrends';
import { CompositeExplainer } from '@/components/research/CompositeExplainer';
import { AppNav } from '@/components/AppNav';
import { PageTabs, useUrlTab } from '@/components/PageTabs';
import { DATA_SURFACES } from '@/lib/data-surfaces';
import { fetchWithCache } from '@/lib/use-resource';
import { useRouter } from 'next/navigation';

const RESEARCH_TABS = DATA_SURFACES.filter(s => s.href === '/research-desk' && s.kind === 'tab');

export default function ResearchDeskPage() {
  const [velocity, setVelocity] = useState<VelocityFile | null>(null);
  const [fit, setFit] = useState<FitFile | null>(null);
  const [notes, setNotes] = useState<ResearchNotesFile | null>(null);
  const [showMovers, setShowMovers] = useState(false);
  const [viewDate, setViewDate] = useState<string | null>(null); // null = today
  const [activeIdea, setActiveIdea] = useState<string | null>(null);
  const [tab, setTab] = useUrlTab(RESEARCH_TABS, 'today');
  const router = useRouter();

  // Legacy URL redirects:
  //   ?tab=overnight → today  (overnight merged into today 2026-05-04)
  //   ?tab=track-record → /alpha-zone  (split into top-level route 2026-05-06)
  //   ?tab=alpha-zone → /alpha-zone   (split into top-level route 2026-05-06)
  useEffect(() => {
    if (tab === 'overnight') setTab('today');
    else if (tab === 'track-record' || tab === 'alpha-zone') router.replace('/alpha-zone');
  }, [tab, setTab, router]);

  useEffect(() => {
    Promise.all([
      fetchWithCache<VelocityFile>('/api/score-velocity'),
      fetchWithCache<FitFile>('/api/portfolio-fit'),
      fetchWithCache<ResearchNotesFile>('/api/research-notes'),
    ]).then(([v, f, n]) => {
      setVelocity(v);
      setFit(f);
      setNotes(n);
    });
  }, []);

  const velocityBy = useMemo(() => {
    const m = new Map<string, VelocityRow>();
    if (velocity) for (const r of velocity.rows) m.set(r.symbol, r);
    return m;
  }, [velocity]);

  const fitBy = useMemo(() => {
    const m = new Map<string, FitRow>();
    if (fit) for (const r of fit.rows) m.set(r.symbol, r);
    return m;
  }, [fit]);

  const publishedDates = useMemo(() => notes ? notedDates(notes) : [], [notes]);
  const today = velocity?.asOf || notes?.lastRunDate || null;
  const isHistorical = viewDate != null && viewDate !== today;

  const book = useMemo(() => {
    if (!notes) return { open: [], closed: [], hasData: false };
    if (!viewDate || viewDate === today) {
      return { open: notes.open || [], closed: notes.closed || [], hasData: true };
    }
    return reconstructAtDate(notes, viewDate);
  }, [notes, viewDate, today]);

  if (!velocity || !fit || !notes) {
    return <div className="p-8 text-[#94a3b8]">Loading research desk…</div>;
  }

  const open = book.open;
  const closed = book.closed;
  // Portfolio symbol set is used to label actions correctly. For names
  // ALREADY HELD: action reads "MAINTAIN" (the desk reiterates, doesn't
  // recommend scaling up). For names NOT held: "OPEN POSITION". Swap
  // ideas keep "SWAP FROM X". Steve directive: "are you saying i should
  // buy more or just rating apple as a buy?" — the latter, and the UI
  // now says so explicitly.
  const portfolioSet = new Set(fit.portfolio);
  // Today's Actions (the headline strip at the top) is meant for ACTIONABLE
  // recommendations only — the desk's BUY/SELL calls. HOLD reiterations (low
  // conviction status=reiterating) are not actions; they stay visible in the
  // "Open Ideas" section below where their semantics fit. Steve directive:
  // a row labeled HOLD with an ADD pill contradicts itself.
  const actionable = open.filter(idea => deriveRating(idea) !== 'HOLD');
  const minDate = publishedDates[0];
  const maxDate = publishedDates[publishedDates.length - 1] || today || undefined;
  const activeSurface = RESEARCH_TABS.find(s => s.id === tab) ?? RESEARCH_TABS[0];

  return (
    <div className="min-h-screen bg-[#0a0e14] text-[#e2e8f0]">
      <AppNav />
      <div className="max-w-5xl mx-auto px-6 pt-2 pb-6 md:px-8 md:pb-8">
        <div className="sticky top-[36px] z-40 bg-[#0a0e14] -mx-6 px-6 md:-mx-8 md:px-8 pt-1 pb-2 border-b border-[#1e293b] shadow-[0_4px_8px_-4px_rgba(0,0,0,0.4)]">
          <header className="mb-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
              <h1 className="text-xl font-bold text-white">Research</h1>
              {tab === 'today' && (
                <div className="flex items-center gap-2 text-[11px]">
                  <label className="text-[#64748b] uppercase tracking-wide">View date</label>
                  <select
                    value={viewDate || today || ''}
                    onChange={(e) => setViewDate(e.target.value === today ? null : e.target.value)}
                    className="bg-[#0d1117] border border-[#1e293b] rounded px-2 py-1 text-[#e2e8f0] tabular-nums focus:outline-none focus:border-amber-500/50 cursor-pointer"
                  >
                    {publishedDates.length === 0 && today && (
                      <option value={today}>{today} (latest)</option>
                    )}
                    {[...publishedDates].reverse().map((d) => (
                      <option key={d} value={d}>
                        {d}{d === today ? ' (latest)' : ''}
                      </option>
                    ))}
                  </select>
                  {isHistorical ? (
                    <button
                      onClick={() => setViewDate(null)}
                      className="text-[10px] text-amber-400 hover:text-amber-300 tracking-wide uppercase"
                    >
                      Back to today
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </header>

          <PageTabs surfaces={RESEARCH_TABS} active={tab} onChange={setTab} />

          {activeSurface && (
            <p className="text-[11px] text-[#64748b] mt-2 italic">{activeSurface.label}</p>
          )}
        </div>
        <div className="mb-4" />

        {/* ─── Today ──────────────────────────────────────────────────── */}
        {tab === 'today' && (
          <>
            {isHistorical && (
              <p className="text-[12px] text-amber-400 mb-4">
                <span className="font-semibold">Historical view: </span>
                <span className="font-mono">{viewDate}</span> · {open.length} open / {closed.length} closed by then ·{' '}
                <span className="text-[#64748b]">notes show snapshot drivers from that day, not live data</span>
              </p>
            )}

            {/* Today's Actions hoisted above the memo (2026-05-04) — the
                desk's published ideas are the headline output and should be
                visible without scrolling. Memo + explainer are deeper reading
                below. Pills carry rating + composite so the row reads as a
                full summary on its own. */}
            <section className="mb-8">
              <h2 className="text-[14px] font-bold text-white tracking-wide uppercase mb-3">
                Today&apos;s Actions
                {actionable.length > 0 && <span className="text-[#64748b] font-normal ml-2">· {actionable.length}</span>}
              </h2>
              {actionable.length === 0 ? (
                <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-6 text-[#64748b]">
                  <p className="text-[13px] text-[#cbd5e1] mb-2">No qualifying ideas today. The desk read the book.</p>
                  <p className="text-[11px] leading-relaxed max-w-2xl mb-2">
                    Steve runs a buy-and-hold book. Trading is friction; only a great idea earns the turnover. The desk publishes when a name clears <strong>all</strong> of: composite ≥ 3.0, positive 5-day acceleration, low correlation to the book, AND an incremental-impact gate (ΔSharpe ≥ +0.02 AND ΔE[r] non-negative).
                  </p>
                  <p className="text-[11px] leading-relaxed max-w-2xl">Quiet days are the right answer.</p>
                  {open.length > actionable.length && (
                    <p className="text-[11px] leading-relaxed max-w-2xl mt-2 text-[#94a3b8]">
                      {open.length - actionable.length} HOLD-rated reiteration{open.length - actionable.length === 1 ? '' : 's'} carried forward — see <strong>Open Ideas</strong> below for the running list.
                    </p>
                  )}
                </div>
              ) : isHistorical ? (
                <div className="space-y-6">
                  {actionable.map((idea) => (
                    <IdeaCard key={idea.symbol} idea={idea} velocity={null} fit={null} swapSourceVelocity={null} historical={true} />
                  ))}
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Column-aligned grid layout (2026-05-22 Steve directive — "ACTION
                      should be lined up below the heading"). 5 columns: rating,
                      composite, ticker, name, action — header row sits above with
                      uppercase labels so the action verb (add / swap from X) reads
                      cleanly in its own column. */}
                  <div className="border border-[#1e293b] rounded-md overflow-hidden">
                    <div className="grid grid-cols-[64px_56px_72px_1fr_180px] gap-3 px-3 py-2 text-[9px] uppercase tracking-widest text-[#64748b] font-semibold border-b border-[#1e293b] bg-[#0a0e14]">
                      <div>Rating</div>
                      <div className="text-right">Comp</div>
                      <div>Ticker</div>
                      <div>Name</div>
                      <div>Action</div>
                    </div>
                    {actionable.map((idea) => {
                      const active = activeIdea === idea.symbol;
                      const rating = deriveRating(idea);
                      const lastNote = idea.notes[idea.notes.length - 1];
                      const composite = velocityBy.get(idea.symbol)?.composite ?? lastNote?.drivers.composite ?? null;
                      const ratingColor = rating === 'BUY'
                        ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                        : rating === 'SELL'
                          ? 'bg-red-500/15 text-red-300 border-red-500/40'
                          : 'bg-[#1e293b] text-[#cbd5e1] border-[#334155]';
                      return (
                        <div
                          key={idea.symbol}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveIdea(active ? null : idea.symbol)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveIdea(active ? null : idea.symbol); }}
                          className={`w-full grid grid-cols-[64px_56px_72px_1fr_180px] gap-3 items-center px-3 py-2 border-b border-[#1e293b]/60 last:border-b-0 text-left transition-colors cursor-pointer ${
                            active
                              ? 'bg-amber-400/10 border-l-2 border-l-amber-400'
                              : 'hover:bg-[#1e293b]/40'
                          }`}
                        >
                          <span className={`text-[10px] font-bold tracking-[0.1em] px-1.5 py-0.5 rounded border w-fit ${ratingColor}`}>
                            {rating}
                          </span>
                          <span className="text-[11px] font-mono tabular-nums text-[#cbd5e1] text-right">
                            {composite != null ? composite.toFixed(2) : '—'}
                          </span>
                          <span className="font-mono font-bold text-[14px] text-white inline-flex items-center gap-1">
                            {idea.symbol}
                            <SignalDot symbol={idea.symbol} size={6} />
                          </span>
                          <span className="text-[11px] text-[#94a3b8] truncate">{idea.name}</span>
                          <span className="text-[11px] uppercase tracking-wider text-[#cbd5e1]">
                            {idea.type === 'swap' && idea.swapSource ? (
                              <>swap from <span className="font-mono font-semibold text-amber-300">{idea.swapSource}</span></>
                            ) : portfolioSet.has(idea.symbol) ? (
                              <span className="font-semibold text-amber-300" title="Already held — desk reiterates the position; not a recommendation to add">maintain</span>
                            ) : (
                              <span className="font-semibold text-emerald-300">open position</span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {/* IdeaCard moved out of inline expansion 2026-05-23 — now
                      renders as a centered modal overlay. Steve directive:
                      "i would like to be able to click on either the buy
                      button or the apple symbol to open the research as a
                      pop up." See the <IdeaModal> render block below. */}
                  {!activeIdea && (
                    <div className="text-[11px] text-[#64748b] italic">Click a row to open the full thesis.</div>
                  )}
                </div>
              )}
            </section>

            {/* Open + Closed ideas log — moved here from the Alpha Zone tab
                (2026-05-05). Open ideas are the desk's pending recommendations;
                Closed Ideas are the historical track record (paid_off /
                violated / superseded). Alpha Zone tab is reserved for basket
                content only. */}
            {!isHistorical && (
              <>
                <section className="mb-6">
                  <h2 className="text-[14px] font-semibold text-white tracking-wide uppercase mb-3">Open Ideas</h2>
                  <p className="text-[11px] text-[#64748b] mb-3">
                    {open.length} open idea{open.length === 1 ? '' : 's'} · pending recommendations from the desk. Swap ideas show the holding they would displace; add ideas are net-new positions. Recommendations are not auto-executed.
                  </p>
                  <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4">
                    <OpenIdeasTable
                      open={open}
                      activeSymbol={activeIdea}
                      portfolio={portfolioSet}
                      onSelect={(sym) => {
                        // Clicking an Open Ideas row opens the IdeaCard
                        // thesis as a modal popup. Reaches the HOLD-rated
                        // reiterations (GLPI / DECK) which aren't surfaced
                        // in the Today's Actions strip.
                        setActiveIdea(prev => prev === sym ? null : sym);
                      }}
                    />
                  </div>
                </section>

                <section className="mb-6">
                  <h2 className="text-[14px] font-semibold text-white tracking-wide uppercase mb-3">Closed Ideas</h2>
                  <p className="text-[11px] text-[#64748b] mb-3">
                    {closed.length} closed idea{closed.length === 1 ? '' : 's'} · auto-closed as paid_off (5 alpha-zone days), violated (sharp composite drop), or superseded.
                  </p>
                  <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4">
                    <TrackRecord closed={closed} />
                  </div>
                </section>
              </>
            )}

            <CompositeExplainer />

            {!isHistorical && (
              <section className="mb-6">
                <button
                  onClick={() => setShowMovers(s => !s)}
                  className="text-[11px] text-[#64748b] hover:text-white tracking-wide uppercase mb-2 flex items-center gap-1.5"
                >
                  <span>{showMovers ? '▾' : '▸'}</span>
                  Internal feed: all movers ({velocity.movers.upTop20.length + velocity.movers.downTop20.length})
                </button>
                {showMovers && (
                  <div className="bg-[#0d1117] border border-[#1e293b] rounded-md overflow-x-auto">
                    <MoversTable
                      rows={velocity.rows.filter(r => velocity.movers.upTop20.includes(r.symbol)).sort((a, b) => {
                        const ka = a.accel5 ?? a.d5.dcomp ?? 0;
                        const kb = b.accel5 ?? b.d5.dcomp ?? 0;
                        return kb - ka;
                      })}
                      fitBySymbol={fitBy}
                      portfolio={new Set(fit.portfolio)}
                      rankBy={velocity.rows.some(r => r.accel5 != null) ? 'accel5' : 'd5'}
                    />
                  </div>
                )}
                <p className="text-[10px] text-[#475569] mt-2 leading-relaxed">
                  The movers feed is the desk&apos;s internal input. The active ideas above are what the desk has chosen to act on.
                </p>
              </section>
            )}

            <p className="text-[10px] text-[#475569] mt-6 leading-relaxed">
              Notes refresh once daily after the 6 AM model run. Last refresh{' '}
              <span className="text-[#94a3b8] font-mono">{notes.lastRunDate || 'never'}</span>.
              {publishedDates.length > 0 ? <> Calendar covers <span className="font-mono">{minDate}</span> → <span className="font-mono">{maxDate}</span>.</> : null}
              {' '}Ideas auto-close as paid_off after 5 alpha-zone days, or as violated on a sharp composite drop.
            </p>
          </>
        )}

        {/* Alpha Zone moved to its own top-level route /alpha-zone (2026-05-06).
            The legacy ?tab=alpha-zone URL is redirected by the useEffect above. */}

        {/* ─── Movers — 5 ranked cross-scope lists (2026-05-22) ─── */}
        {tab === 'movers' && <MoversTab />}

        {/* ─── Alpha Zone Reports — full research note per Alpha Zone holding ─── */}
        {tab === 'alpha-reports' && <AlphaZoneReports />}

        {/* ─── Peer Group Trends — monthly Q + V mean ± 1σ per peer group ─── */}
        {tab === 'peer-group-trends' && <PeerGroupTrends />}
      </div>

      {/* Idea thesis modal (2026-05-23) — centered popup that opens when
          activeIdea is set. Triggered by Today's Actions row clicks and
          Open Ideas row clicks. Backdrop click + Esc close it. */}
      {activeIdea && open.find(i => i.symbol === activeIdea) && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          onClick={() => setActiveIdea(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setActiveIdea(null); }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={`Research thesis for ${activeIdea}`}
        >
          <div
            className="bg-[#0a0e14] border border-[var(--gold)]/40 rounded-lg shadow-2xl max-w-5xl w-full my-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveIdea(null)}
              className="absolute top-3 right-3 z-10 text-[11px] text-[#94a3b8] hover:text-white tracking-wide uppercase border border-[#1e293b] hover:border-[#475569] rounded px-2.5 py-1 bg-[#0d1117]/80"
              title="Close (Esc)"
            >
              close ✕
            </button>
            <div className="p-5">
              <IdeaCard
                key={activeIdea}
                idea={open.find(i => i.symbol === activeIdea)!}
                velocity={velocityBy.get(activeIdea) || null}
                fit={fitBy.get(activeIdea) || null}
                swapSourceVelocity={(() => {
                  const idea = open.find(i => i.symbol === activeIdea)!;
                  return idea.swapSource ? velocityBy.get(idea.swapSource) || null : null;
                })()}
                historical={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

